import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Button, Empty, Spin, Tag, message, Alert } from "antd";
import {
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../const";

const ScheduleInterviewModal = ({ visible, talentId, talentName, onClose, onSuccess }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (visible && talentId) {
      fetchJobs();
      setSelectedJobIds([]);
      setResults(null);
    }
  }, [visible, talentId]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        API_CONST.GET_JOB_REQUIREMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const allJobs = res.data.Response || [];
      const activeJobs = allJobs.filter((j) =>
        !["draft", "closed", "filled", "rejected"].includes((j.status || "").toLowerCase())
      );
      setJobs(activeJobs);
    } catch {
      message.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedJobIds.length) {
      message.warning("Please select at least one job");
      return;
    }
    setSubmitting(true);
    const success = [];
    const failed = [];
    const token = localStorage.getItem("authToken");
    for (const jobId of selectedJobIds) {
      try {
        await axios.post(
          API_CONST.CREATE_INTERVIEW_REQUEST,
          { jobId, talentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const job = jobs.find((j) => j.id === jobId);
        success.push(job?.role || jobId);
      } catch (err) {
        const job = jobs.find((j) => j.id === jobId);
        failed.push({
          label: job?.role || jobId,
          reason: err.response?.data?.message || "Failed to send request",
        });
      }
    }
    setSubmitting(false);
    setResults({ success, failed });
    if (success.length > 0 && onSuccess) onSuccess();
  };

  const isResultView = !!results;

  return (
    <Modal
      title={
        <span>
          <VideoCameraOutlined style={{ color: "#014c75", marginRight: 8 }} />
          Schedule Interview — {talentName}
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={
        isResultView ? (
          <Button
            type="primary"
            onClick={onClose}
            style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={submitting}
              disabled={!selectedJobIds.length}
              onClick={handleSubmit}
              style={{ background: "#014c75", borderColor: "#014c75" }}
            >
              Send Request{selectedJobIds.length > 0 ? ` (${selectedJobIds.length})` : ""}
            </Button>
          </>
        )
      }
      width={560}
      destroyOnClose
    >
      {isResultView ? (
        <div>
          {results.success.length > 0 && (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              message={
                <span>
                  Interview request sent for:{" "}
                  <strong>{results.success.join(", ")}</strong>
                </span>
              }
              description="The partner will be notified to accept or reject. You can track status under Interview Requests."
              style={{ marginBottom: 12 }}
              showIcon
            />
          )}
          {results.failed.map((f, i) => (
            <Alert
              key={i}
              type="error"
              icon={<CloseCircleOutlined />}
              message={
                <span>
                  <strong>{f.label}</strong>: {f.reason}
                </span>
              }
              style={{ marginBottom: 8 }}
              showIcon
            />
          ))}
        </div>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <p style={{ color: "#999", marginTop: 12 }}>Loading your open jobs...</p>
        </div>
      ) : jobs.length === 0 ? (
        <Empty
          description={
            <span>
              No open jobs found.{" "}
              <a href="/customer/post-job">Post a job</a> first to request an interview.
            </span>
          }
        />
      ) : (
        <div>
          <p style={{ color: "#595959", marginBottom: 16 }}>
            Select the job(s) for which you want to request an interview with{" "}
            <strong>{talentName}</strong>. The partner will be notified and can accept or reject.
          </p>
          <Checkbox.Group
            style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10 }}
            value={selectedJobIds}
            onChange={setSelectedJobIds}
          >
            {jobs.map((job) => (
              <div
                key={job.id}
                style={{
                  padding: "12px 16px",
                  border: "1px solid #e8e8e8",
                  borderRadius: 8,
                  background: "#f8f9fd",
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                }}
              >
                <Checkbox value={job.id}>
                  <div style={{ paddingLeft: 4 }}>
                    <span style={{ fontWeight: 600, color: "#333", fontSize: 14 }}>
                      {job.role}
                    </span>
                    <div style={{ marginTop: 5 }}>
                      {job.engagementType && (
                        <Tag color="blue" style={{ fontSize: 11 }}>
                          {job.engagementType}
                        </Tag>
                      )}
                      {job.reportingLocation && (
                        <Tag color="default" style={{ fontSize: 11 }}>
                          {job.reportingLocation}
                        </Tag>
                      )}
                      <span style={{ color: "#aaa", fontSize: 11, marginLeft: 4 }}>
                        #{job.id}
                      </span>
                    </div>
                  </div>
                </Checkbox>
              </div>
            ))}
          </Checkbox.Group>
        </div>
      )}
    </Modal>
  );
};

export default ScheduleInterviewModal;
